import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Supervisor: floor layout editor (v1.4 — pathway-first)
// - Machines from Master → Machine Group; palette cell-grouped, tiles color-coded
// - Cell HULL rectangles drawn around each cell's tiles
// - Distances are MANUAL: click two machines, type meters (or "no path") → saved
// - Saved walkable path = green line + meters; blocked = red dashed line
// - Scale calibration kept ONLY for the optional visual ruler (not required for distances)

const TILE_W = 120;
const TILE_H = 80;
const CANVAS_W = 1200;
const CANVAS_H = 700;
const GRID = 20;
const HULL_PAD = 14;

const BRAND = {
  orange: "#F99022",
  border: "#e5e7eb",
  bg: "#fafafa",
  textMuted: "#6b7280",
  textDark: "#111827",
  green: "#16a34a",
  red: "#dc2626",
  blue: "#2563eb",
};

const GROUP_COLORS = [
  { bg: "#dbeafe", border: "#2563eb" },
  { bg: "#dcfce7", border: "#16a34a" },
  { bg: "#fef3c7", border: "#d97706" },
  { bg: "#fce7f3", border: "#db2777" },
  { bg: "#e0e7ff", border: "#4f46e5" },
  { bg: "#f3e8ff", border: "#9333ea" },
  { bg: "#ccfbf1", border: "#0d9488" },
  { bg: "#ffedd5", border: "#ea580c" },
];
const UNGROUPED_COLOR = { bg: "#f3f4f6", border: "#6b7280" };

function colorFor(groupId) {
  if (!groupId) return UNGROUPED_COLOR;
  let h = 0;
  for (let i = 0; i < groupId.length; i += 1) {
    h = (h * 31 + groupId.charCodeAt(i)) >>> 0;
  }
  return GROUP_COLORS[h % GROUP_COLORS.length];
}

function cleanCustomerId(raw) {
  if (!raw) return "";
  return String(raw).replace(/\\/g, "").replace(/"/g, "").trim();
}

function snap(n) {
  return Math.round(n / GRID) * GRID;
}

function pairKey(a, b) {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

export default function FloorLayout() {
  const baseRef = useRef("");
  const canvasRef = useRef(null);

  const [machines, setMachines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [settings, setSettings] = useState({ pixels_per_meter: null });
  // pathways[pairKey] = { meters: number|null, status: 'walkable'|'blocked' }
  const [pathways, setPathways] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Connection workflow
  const [selectedDevice, setSelectedDevice] = useState(null); // "from" machine
  const [pathModal, setPathModal] = useState(null); // { deviceA, deviceB, meters, status, isExisting, waypoints }
  // Bends placed while drawing a new path (before second tile is clicked)
  const [drawWaypoints, setDrawWaypoints] = useState([]);
  // Cursor position while drawing (for preview line)
  const [drawCursor, setDrawCursor] = useState(null);
  // Path-edit mode: when a saved path is selected, waypoint handles + insert handles appear
  const [selectedPathKey, setSelectedPathKey] = useState(null);
  // Tile hover (just visual) — used to highlight paths emanating from that machine
  const [hoveredDevice, setHoveredDevice] = useState(null);
  const wpDrag = useRef({ pairKey: null, index: null, moved: false });

  // Page mode: 'view' (read-only: click a machine to see its connections) or
  // 'edit' (click-to-draw, drag tiles, edit paths). Defaults to view.
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("floor_layout_mode") || "view"; }
    catch (_e) { return "view"; }
  });
  useEffect(() => {
    try { localStorage.setItem("floor_layout_mode", mode); } catch (_e) {}
  }, [mode]);
  // Machine clicked in view mode — pinned so its highlights stay visible
  const [viewSelectedDevice, setViewSelectedDevice] = useState(null);

  // Scale calibration (optional — only for visual ruler)
  const [calibMode, setCalibMode] = useState(false);
  const [calibP1, setCalibP1] = useState(null);
  const [calibP2, setCalibP2] = useState(null);
  const [calibAsk, setCalibAsk] = useState(false);
  const [calibMeters, setCalibMeters] = useState("");

  const drag = useRef({ deviceId: null, dx: 0, dy: 0, moved: false });

  const customerId = useMemo(() => {
    const ls = typeof window !== "undefined" ? localStorage.getItem("CustomerID") : "";
    return cleanCustomerId(ls) || window?._env_?.CUSTOMER_ID || "";
  }, []);

  useEffect(() => {
    baseRef.current = (window._env_?.SERVER_URL2 || "").replace(/\/$/, "");
  }, []);

  useEffect(() => {
    if (!customerId) {
      setErr("no customer logged in");
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(
          `${baseRef.current}/api/layout?customer_id=${encodeURIComponent(customerId)}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setMachines(data.machines || []);
        setGroups(data.groups || []);
        setSettings(data.settings || { pixels_per_meter: null });
        const pw = {};
        for (const o of data.distance_overrides || []) {
          pw[pairKey(o.device_a, o.device_b)] = {
            meters: o.meters,
            status: o.status || "walkable",
            waypoints: Array.isArray(o.waypoints) ? o.waypoints : [],
          };
        }
        setPathways(pw);
        setErr("");
      } catch (e) {
        setErr(e?.message || "could not load");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [customerId]);

  const palette = useMemo(() => {
    const buckets = new Map();
    for (const m of machines) {
      if (m.placed) continue;
      const key = m.group_id || "__ungrouped__";
      if (!buckets.has(key)) {
        buckets.set(key, {
          groupId: m.group_id,
          groupName: m.group_name || "Ungrouped",
          color: colorFor(m.group_id),
          machines: [],
        });
      }
      buckets.get(key).machines.push(m);
    }
    return Array.from(buckets.values()).sort((a, b) => {
      if (!a.groupId && b.groupId) return 1;
      if (a.groupId && !b.groupId) return -1;
      return a.groupName.localeCompare(b.groupName);
    });
  }, [machines]);

  const placed = machines.filter((m) => m.placed);
  const unplacedCount = machines.length - placed.length;
  const placedById = useMemo(() => {
    const map = new Map();
    for (const m of placed) map.set(m.device_id, m);
    return map;
  }, [placed]);

  const hulls = useMemo(() => {
    const byGroup = new Map();
    for (const m of placed) {
      if (!m.group_id) continue;
      if (!byGroup.has(m.group_id)) {
        byGroup.set(m.group_id, {
          groupId: m.group_id,
          groupName: m.group_name,
          color: colorFor(m.group_id),
          tiles: [],
        });
      }
      byGroup.get(m.group_id).tiles.push(m);
    }
    return Array.from(byGroup.values()).map((g) => {
      const minX = Math.min(...g.tiles.map((t) => t.x)) - HULL_PAD;
      const minY = Math.min(...g.tiles.map((t) => t.y)) - HULL_PAD;
      const maxX = Math.max(...g.tiles.map((t) => t.x + TILE_W)) + HULL_PAD;
      const maxY = Math.max(...g.tiles.map((t) => t.y + TILE_H)) + HULL_PAD;
      return { ...g, x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    });
  }, [placed]);

  // Path segments for every saved pair where both endpoints are placed.
  // Each segment is a polyline through optional waypoints.
  const pathSegments = useMemo(() => {
    const out = [];
    for (const key of Object.keys(pathways)) {
      const [a, b] = key.split("|");
      const ma = placedById.get(a);
      const mb = placedById.get(b);
      if (!ma || !mb) continue;
      const startX = ma.x + TILE_W / 2;
      const startY = ma.y + TILE_H / 2;
      const endX = mb.x + TILE_W / 2;
      const endY = mb.y + TILE_H / 2;
      const wps = Array.isArray(pathways[key].waypoints) ? pathways[key].waypoints : [];
      const polyPoints = [{ x: startX, y: startY }, ...wps, { x: endX, y: endY }];
      // Total polyline length in pixels
      let totalPx = 0;
      for (let i = 1; i < polyPoints.length; i += 1) {
        totalPx += Math.hypot(
          polyPoints[i].x - polyPoints[i - 1].x,
          polyPoints[i].y - polyPoints[i - 1].y,
        );
      }
      // Label sits at the arc-length midpoint of the WHOLE polyline, OFFSET
      // perpendicular to the path direction at that point. The offset is what
      // separates labels whose paths share an endpoint (e.g. several paths
      // converging on the same machine) so they don't pile on top of each other.
      let midX;
      let midY;
      {
        const half = totalPx / 2;
        let acc = 0;
        midX = polyPoints[0].x;
        midY = polyPoints[0].y;
        let dirX = 1;
        let dirY = 0;
        for (let i = 1; i < polyPoints.length; i += 1) {
          const sdx = polyPoints[i].x - polyPoints[i - 1].x;
          const sdy = polyPoints[i].y - polyPoints[i - 1].y;
          const segLen = Math.hypot(sdx, sdy);
          if (acc + segLen >= half) {
            const t = segLen === 0 ? 0 : (half - acc) / segLen;
            midX = polyPoints[i - 1].x + t * sdx;
            midY = polyPoints[i - 1].y + t * sdy;
            if (segLen > 0) {
              dirX = sdx / segLen;
              dirY = sdy / segLen;
            }
            break;
          }
          acc += segLen;
        }
        // Perpendicular unit vector — pick the side closer to the canvas center
        // so labels prefer "inside" the layout rather than spilling off-edge.
        let perpX = -dirY;
        let perpY = dirX;
        const cx = CANVAS_W / 2;
        const cy = CANVAS_H / 2;
        if ((midX + perpX - cx) ** 2 + (midY + perpY - cy) ** 2
            > (midX - perpX - cx) ** 2 + (midY - perpY - cy) ** 2) {
          perpX = -perpX;
          perpY = -perpY;
        }
        const LABEL_OFFSET = 16;
        midX += perpX * LABEL_OFFSET;
        midY += perpY * LABEL_OFFSET;
      }
      out.push({
        pairKey: key,
        deviceA: a,
        deviceB: b,
        nameA: ma.name,
        nameB: mb.name,
        polyPoints,
        startX, startY, endX, endY,
        midX, midY,
        totalPx,
        meters: pathways[key].meters,
        status: pathways[key].status || "walkable",
        waypoints: wps,
      });
    }
    return out;
  }, [pathways, placedById]);

  // Persist waypoint changes. If the ruler is calibrated and path is walkable,
  // also recompute the displayed distance from the polyline geometry so it
  // stays accurate as you move bends.
  const saveWaypoints = async (pairKeyStr, newWaypoints) => {
    const [a, b] = pairKeyStr.split("|");
    const existing = pathways[pairKeyStr];
    if (!existing) return;
    let newMeters = existing.meters;
    if (settings.pixels_per_meter && existing.status === "walkable") {
      const px = polylinePixels(a, b, newWaypoints);
      newMeters = Math.round((px / settings.pixels_per_meter) * 10) / 10;
    }
    try {
      const res = await fetch(`${baseRef.current}/api/layout/distance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          device_a: a,
          device_b: b,
          meters: newMeters,
          status: existing.status,
          waypoints: newWaypoints,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Reflect recomputed meters in local state
      setPathways((p) => {
        const cur = p[pairKeyStr];
        if (!cur) return p;
        return { ...p, [pairKeyStr]: { ...cur, meters: newMeters } };
      });
      setErr("");
    } catch (e) {
      setErr(e?.message || "could not save waypoints");
    }
  };

  // Local + remote update of waypoints array for a pair
  const updateWaypoints = (pairKeyStr, transform, persist = true) => {
    setPathways((p) => {
      const cur = p[pairKeyStr];
      if (!cur) return p;
      const nextWps = transform(Array.isArray(cur.waypoints) ? cur.waypoints : []);
      if (persist) {
        // fire and forget
        saveWaypoints(pairKeyStr, nextWps);
      }
      return { ...p, [pairKeyStr]: { ...cur, waypoints: nextWps } };
    });
  };

  // Waypoint drag handlers
  const onWaypointMouseDown = (e, pairKeyStr, index) => {
    if (calibMode) return;
    e.preventDefault();
    e.stopPropagation();
    wpDrag.current = { pairKey: pairKeyStr, index, moved: false };
    window.addEventListener("mousemove", onWaypointMove);
    window.addEventListener("mouseup", onWaypointUp);
  };

  const onWaypointMove = useCallback((e) => {
    const d = wpDrag.current;
    if (!d.pairKey || d.index == null || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(CANVAS_W, Math.round(e.clientX - rect.left)));
    const y = Math.max(0, Math.min(CANVAS_H, Math.round(e.clientY - rect.top)));
    d.moved = true;
    setPathways((p) => {
      const cur = p[d.pairKey];
      if (!cur) return p;
      const wps = Array.isArray(cur.waypoints) ? [...cur.waypoints] : [];
      wps[d.index] = { x, y };
      return { ...p, [d.pairKey]: { ...cur, waypoints: wps } };
    });
  }, []);

  const onWaypointUp = useCallback(() => {
    const d = wpDrag.current;
    if (d.pairKey && d.moved) {
      // Persist final position
      setPathways((p) => {
        const cur = p[d.pairKey];
        if (cur) saveWaypoints(d.pairKey, cur.waypoints || []);
        return p;
      });
    }
    wpDrag.current = { pairKey: null, index: null, moved: false };
    window.removeEventListener("mousemove", onWaypointMove);
    window.removeEventListener("mouseup", onWaypointUp);
  }, [onWaypointMove]);

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onWaypointMove);
      window.removeEventListener("mouseup", onWaypointUp);
    };
  }, [onWaypointMove, onWaypointUp]);

  const insertWaypoint = (pairKeyStr, afterIndex, point) => {
    updateWaypoints(pairKeyStr, (wps) => {
      const next = [...wps];
      next.splice(afterIndex, 0, point);
      return next;
    });
  };

  const removeWaypoint = (pairKeyStr, index) => {
    updateWaypoints(pairKeyStr, (wps) => wps.filter((_w, i) => i !== index));
  };

  // Place from palette
  const placeMachine = (deviceId) => {
    setMachines((ms) =>
      ms.map((m) => {
        if (m.device_id !== deviceId) return m;
        const idx = ms.filter((x) => x.placed).length;
        const col = idx % 8;
        const row = Math.floor(idx / 8);
        return {
          ...m,
          placed: true,
          x: snap(40 + col * (TILE_W + 20)),
          y: snap(40 + row * (TILE_H + 20)),
        };
      }),
    );
    setDirty(true);
  };

  const placeGroup = (groupId) => {
    setMachines((ms) => {
      const placedCount = ms.filter((x) => x.placed).length;
      let i = placedCount;
      return ms.map((m) => {
        if (m.placed) return m;
        const matches = groupId == null ? !m.group_id : m.group_id === groupId;
        if (!matches) return m;
        const col = i % 8;
        const row = Math.floor(i / 8);
        i += 1;
        return {
          ...m,
          placed: true,
          x: snap(40 + col * (TILE_W + 20)),
          y: snap(40 + row * (TILE_H + 20)),
        };
      });
    });
    setDirty(true);
  };

  const unplaceMachine = (deviceId) => {
    setMachines((ms) =>
      ms.map((m) => (m.device_id === deviceId ? { ...m, placed: false, x: 0, y: 0 } : m)),
    );
    if (selectedDevice === deviceId) setSelectedDevice(null);
    setDirty(true);
  };

  // Drag handling — distinguishes click from drag via movement threshold
  const onTileMouseDown = (e, m) => {
    if (calibMode) return;
    if (mode === "view") return; // no drag in view mode
    if (!canvasRef.current) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    drag.current = {
      deviceId: m.device_id,
      dx: e.clientX - rect.left - m.x,
      dy: e.clientY - rect.top - m.y,
      moved: false,
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = useCallback((e) => {
    const d = drag.current;
    if (!d.deviceId || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    let nx = e.clientX - rect.left - d.dx;
    let ny = e.clientY - rect.top - d.dy;
    nx = Math.max(0, Math.min(CANVAS_W - TILE_W, snap(nx)));
    ny = Math.max(0, Math.min(CANVAS_H - TILE_H, snap(ny)));
    setMachines((ms) =>
      ms.map((m) => {
        if (m.device_id !== d.deviceId) return m;
        if (m.x !== nx || m.y !== ny) d.moved = true;
        return { ...m, x: nx, y: ny };
      }),
    );
  }, []);

  const onMouseUp = useCallback(() => {
    if (drag.current.deviceId && drag.current.moved) setDirty(true);
    drag.current = { deviceId: null, dx: 0, dy: 0, moved: false };
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  // Compute polyline length in pixels (machine center → bends → other machine center)
  const polylinePixels = (deviceA, deviceB, waypoints) => {
    const a = placedById.get(deviceA);
    const b = placedById.get(deviceB);
    if (!a || !b) return 0;
    const pts = [
      { x: a.x + TILE_W / 2, y: a.y + TILE_H / 2 },
      ...waypoints,
      { x: b.x + TILE_W / 2, y: b.y + TILE_H / 2 },
    ];
    let total = 0;
    for (let i = 1; i < pts.length; i += 1) {
      total += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    }
    return total;
  };

  const pxToM = (px) => (settings.pixels_per_meter ? px / settings.pixels_per_meter : null);

  // Tile click — drives the draw-as-you-click workflow (edit mode)
  // or pin-to-show-connections (view mode)
  const onTileClick = (m) => {
    if (calibMode) return;
    if (drag.current.moved) return; // ignore click after drag
    // VIEW MODE: clicking just toggles the connections-highlight pin
    if (mode === "view") {
      setViewSelectedDevice((v) => (v === m.device_id ? null : m.device_id));
      return;
    }
    if (!selectedDevice) {
      setSelectedDevice(m.device_id);
      setDrawWaypoints([]);
      setDrawCursor(null);
      return;
    }
    if (selectedDevice === m.device_id) {
      cancelDraw();
      return;
    }
    // Different tile → end the path. Use the drawn waypoints (reversed if needed to match sorted storage).
    const key = pairKey(selectedDevice, m.device_id);
    const existing = pathways[key];
    let savedWps = existing?.waypoints ?? [];
    if (drawWaypoints.length > 0) {
      const [sortedA] = selectedDevice < m.device_id ? [selectedDevice, m.device_id] : [m.device_id, selectedDevice];
      savedWps = sortedA === selectedDevice ? [...drawWaypoints] : [...drawWaypoints].reverse();
    }

    // Auto-compute distance from the polyline length if the ruler is calibrated.
    if (settings.pixels_per_meter) {
      const px = polylinePixels(selectedDevice, m.device_id, savedWps);
      const meters = Math.round((px / settings.pixels_per_meter) * 10) / 10; // 1 decimal
      // Save directly, no modal interruption.
      (async () => {
        const ok = await submitPath({
          device_a: selectedDevice,
          device_b: m.device_id,
          meters,
          status: "walkable",
          waypoints: savedWps,
        });
        if (ok) {
          setPathways((p) => ({
            ...p,
            [key]: { meters, status: "walkable", waypoints: savedWps },
          }));
          setErr("");
        }
      })();
      setSelectedDevice(null);
      setDrawWaypoints([]);
      setDrawCursor(null);
      return;
    }

    // Scale not set → fall back to the manual modal
    setPathModal({
      deviceA: selectedDevice,
      deviceB: m.device_id,
      nameA: placedById.get(selectedDevice)?.name,
      nameB: placedById.get(m.device_id)?.name,
      meters: existing?.meters != null ? String(existing.meters) : "",
      status: existing?.status || "walkable",
      isExisting: !!existing,
      waypoints: savedWps,
    });
    setSelectedDevice(null);
    setDrawWaypoints([]);
    setDrawCursor(null);
  };

  // Live computed distance while drawing (for the floating chip near cursor)
  const drawLivePx = useMemo(() => {
    if (!selectedDevice) return 0;
    const start = placedById.get(selectedDevice);
    if (!start) return 0;
    const sx = start.x + TILE_W / 2;
    const sy = start.y + TILE_H / 2;
    const pts = [{ x: sx, y: sy }, ...drawWaypoints, ...(drawCursor ? [drawCursor] : [])];
    let total = 0;
    for (let i = 1; i < pts.length; i += 1) {
      total += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    }
    return total;
  }, [selectedDevice, drawWaypoints, drawCursor, placedById]);

  // Quick "no path" while drawing — saves immediately, no meters
  const markDrawingAsNoPath = async (deviceB) => {
    if (!selectedDevice || !deviceB) return;
    const key = pairKey(selectedDevice, deviceB);
    let savedWps = [];
    if (drawWaypoints.length > 0) {
      const [sortedA] = selectedDevice < deviceB ? [selectedDevice, deviceB] : [deviceB, selectedDevice];
      savedWps = sortedA === selectedDevice ? [...drawWaypoints] : [...drawWaypoints].reverse();
    }
    const ok = await submitPath({
      device_a: selectedDevice,
      device_b: deviceB,
      meters: null,
      status: "blocked",
      waypoints: savedWps,
    });
    if (ok) {
      setPathways((p) => ({
        ...p,
        [key]: { meters: null, status: "blocked", waypoints: savedWps },
      }));
      cancelDraw();
      setErr("");
    }
  };

  // Open the modal from clicking an existing path label
  const openLabelModal = (seg) => {
    setPathModal({
      deviceA: seg.deviceA,
      deviceB: seg.deviceB,
      nameA: seg.nameA,
      nameB: seg.nameB,
      meters: seg.meters != null ? String(seg.meters) : "",
      status: seg.status,
      isExisting: true,
    });
  };

  const closeModal = () => setPathModal(null);

  const submitPath = async (body) => {
    try {
      const res = await fetch(`${baseRef.current}/api/layout/distance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerId, ...body }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.message || `HTTP ${res.status}`);
      }
      return true;
    } catch (e) {
      setErr(e?.message || "could not save");
      return false;
    }
  };

  const savePathWalkable = async () => {
    if (!pathModal) return;
    const meters = parseFloat(pathModal.meters);
    if (!Number.isFinite(meters) || meters < 0) {
      setErr("Enter walking distance in meters (e.g. 8)");
      return;
    }
    const wps = Array.isArray(pathModal.waypoints) ? pathModal.waypoints : [];
    const ok = await submitPath({
      device_a: pathModal.deviceA,
      device_b: pathModal.deviceB,
      meters,
      status: "walkable",
      waypoints: wps,
    });
    if (ok) {
      setPathways((p) => ({
        ...p,
        [pairKey(pathModal.deviceA, pathModal.deviceB)]: {
          meters, status: "walkable", waypoints: wps,
        },
      }));
      setErr("");
      closeModal();
    }
  };

  const savePathBlocked = async () => {
    if (!pathModal) return;
    const wps = Array.isArray(pathModal.waypoints) ? pathModal.waypoints : [];
    const ok = await submitPath({
      device_a: pathModal.deviceA,
      device_b: pathModal.deviceB,
      meters: null,
      status: "blocked",
      waypoints: wps,
    });
    if (ok) {
      setPathways((p) => ({
        ...p,
        [pairKey(pathModal.deviceA, pathModal.deviceB)]: {
          meters: null, status: "blocked", waypoints: wps,
        },
      }));
      setErr("");
      closeModal();
    }
  };

  const deletePath = async () => {
    if (!pathModal) return;
    const ok = await submitPath({
      device_a: pathModal.deviceA,
      device_b: pathModal.deviceB,
      delete: true,
    });
    if (ok) {
      setPathways((p) => {
        const n = { ...p };
        delete n[pairKey(pathModal.deviceA, pathModal.deviceB)];
        return n;
      });
      setErr("");
      closeModal();
    }
  };

  // Layout positions save (unchanged from v1.3)
  const save = async () => {
    if (saving) return;
    setSaving(true);
    setErr("");
    try {
      const body = {
        customer_id: customerId,
        machines: machines.map((m) => ({
          device_id: m.device_id,
          x: m.x, y: m.y, w: m.w, h: m.h, rotation: m.rotation,
          placed: m.placed,
        })),
      };
      const res = await fetch(`${baseRef.current}/api/layout/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLastSaved(data.saved_at);
      setDirty(false);
    } catch (e) {
      setErr(e?.message || "save failed");
    } finally {
      setSaving(false);
    }
  };

  // Canvas click:
  // - In calibration mode → pick scale points
  // - View mode: deselect pinned machine
  // - Edit mode + "from" tile selected → add a bend waypoint
  // - Otherwise → deselect any selected path
  const onCanvasClick = (e) => {
    if (calibMode) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = Math.round(e.clientX - rect.left);
      const y = Math.round(e.clientY - rect.top);
      if (!calibP1) setCalibP1({ x, y });
      else if (!calibP2) {
        setCalibP2({ x, y });
        setCalibAsk(true);
      }
      return;
    }
    if (mode === "view") {
      if (viewSelectedDevice) setViewSelectedDevice(null);
      return;
    }
    if (selectedDevice) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = Math.round(e.clientX - rect.left);
      const y = Math.round(e.clientY - rect.top);
      setDrawWaypoints((wps) => [...wps, { x, y }]);
      return;
    }
    if (selectedPathKey) setSelectedPathKey(null);
  };

  // Track cursor while drawing for the live preview line
  const onCanvasMouseMove = (e) => {
    if (!selectedDevice || calibMode) return;
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setDrawCursor({
      x: Math.round(e.clientX - rect.left),
      y: Math.round(e.clientY - rect.top),
    });
  };

  const undoLastWaypoint = () => {
    setDrawWaypoints((wps) => wps.slice(0, -1));
  };
  const cancelDraw = () => {
    setSelectedDevice(null);
    setDrawWaypoints([]);
    setDrawCursor(null);
  };
  const cancelCalib = () => {
    setCalibMode(false);
    setCalibP1(null);
    setCalibP2(null);
    setCalibAsk(false);
    setCalibMeters("");
  };
  const finishCalib = async () => {
    const meters = parseFloat(calibMeters);
    if (!meters || meters <= 0) {
      setErr("Enter a positive distance in meters");
      return;
    }
    const px = Math.hypot(calibP2.x - calibP1.x, calibP2.y - calibP1.y);
    if (px < 5) {
      setErr("Points too close — pick two points farther apart");
      return;
    }
    const ppm = px / meters;
    try {
      const res = await fetch(`${baseRef.current}/api/layout/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          pixels_per_meter: ppm,
          scale_p1_x: calibP1.x, scale_p1_y: calibP1.y,
          scale_p2_x: calibP2.x, scale_p2_y: calibP2.y,
          scale_meters: meters,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSettings({
        pixels_per_meter: ppm,
        scale_p1_x: calibP1.x, scale_p1_y: calibP1.y,
        scale_p2_x: calibP2.x, scale_p2_y: calibP2.y,
        scale_meters: meters,
      });
      cancelCalib();
      setErr("");
    } catch (e) {
      setErr(e?.message || "could not save scale");
    }
  };

  // ====== Simple List mode helpers ======
  // Sequence is derived from placed machines sorted by (y, x) — adjacent in the
  // list = adjacent in the walking sequence. Reorder swaps y values so canvas
  // mode stays in sync.
  const sequence = useMemo(() => {
    return [...placed].sort((a, b) => a.y - b.y || a.x - b.x);
  }, [placed]);

  const moveInSequence = (index, dir) => {
    if (sequence.length < 2) return;
    const swapIndex = dir === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sequence.length) return;
    const a = sequence[index];
    const b = sequence[swapIndex];
    setMachines((ms) =>
      ms.map((m) => {
        if (m.device_id === a.device_id) return { ...m, x: b.x, y: b.y };
        if (m.device_id === b.device_id) return { ...m, x: a.x, y: a.y };
        return m;
      }),
    );
    setDirty(true);
  };

  // Place a machine into the bottom of the sequence (auto-position in vertical column)
  const placeIntoSequence = (deviceId) => {
    setMachines((ms) => {
      const placedNow = ms.filter((x) => x.placed);
      const lastY = placedNow.length
        ? Math.max(...placedNow.map((p) => p.y))
        : 20;
      const newY = snap(lastY + 100);
      return ms.map((m) => {
        if (m.device_id !== deviceId) return m;
        return { ...m, placed: true, x: 540, y: newY };
      });
    });
    setDirty(true);
  };

  const placeGroupIntoSequence = (groupId) => {
    setMachines((ms) => {
      const placedNow = ms.filter((x) => x.placed);
      let nextY = placedNow.length
        ? Math.max(...placedNow.map((p) => p.y)) + 100
        : 40;
      return ms.map((m) => {
        if (m.placed) return m;
        const matches = groupId == null ? !m.group_id : m.group_id === groupId;
        if (!matches) return m;
        const yy = snap(nextY);
        nextY += 100;
        return { ...m, placed: true, x: 540, y: yy };
      });
    });
    setDirty(true);
  };

  // Inline distance edit (per adjacent pair) — debounced save when user pauses
  const adjacentDistanceSet = async (a, b, meters, status = "walkable") => {
    try {
      const body = { customer_id: customerId, device_a: a, device_b: b };
      if (status === "blocked") {
        body.status = "blocked";
        body.meters = null;
      } else {
        const n = parseFloat(meters);
        if (!Number.isFinite(n) || n < 0) return; // ignore invalid
        body.meters = n;
        body.status = "walkable";
      }
      const res = await fetch(`${baseRef.current}/api/layout/distance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPathways((p) => ({
        ...p,
        [pairKey(a, b)]: {
          meters: body.status === "blocked" ? null : Number(body.meters),
          status: body.status,
        },
      }));
      setErr("");
    } catch (e) {
      setErr(e?.message || "could not save");
    }
  };

  const rulerMeters = useMemo(() => {
    if (!settings.pixels_per_meter) return null;
    const targets = [1, 2, 5, 10, 20, 50, 100];
    let best = 1;
    for (const t of targets) {
      if (t * settings.pixels_per_meter <= 200) best = t;
      else break;
    }
    return best;
  }, [settings.pixels_per_meter]);

  return (
    <div style={{ padding: 16, fontFamily: "system-ui, sans-serif" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: BRAND.textDark }}>
            Floor Layout
          </div>
          <div style={{ fontSize: 13, color: BRAND.textMuted, marginTop: 2 }}>
            {mode === "view"
              ? <>👁 <b>View mode</b> — hover a machine to peek; click to pin and see all distances from it. Switch to Edit to draw/change paths.</>
              : selectedDevice
                ? <>✏️ Drawing from <b style={{ color: BRAND.blue }}>{placedById.get(selectedDevice)?.name}</b> — click empty space to add bends, then click the destination machine.</>
                : <>✏️ <b>Edit mode</b> — click a machine to start a path. Click empty space to draw bends, click another machine to finish.</>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* View / Edit mode toggle */}
          <div
            style={{
              display: "inline-flex",
              border: `1px solid ${BRAND.border}`,
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <button
              onClick={() => {
                setMode("view");
                setSelectedDevice(null);
                setDrawWaypoints([]);
                setSelectedPathKey(null);
              }}
              style={{
                padding: "8px 14px", fontSize: 13, fontWeight: 700,
                border: "none",
                background: mode === "view" ? BRAND.orange : "#fff",
                color: mode === "view" ? "#fff" : BRAND.textMuted,
                cursor: "pointer",
              }}
            >
              👁 View
            </button>
            <button
              onClick={() => {
                setMode("edit");
                setViewSelectedDevice(null);
              }}
              style={{
                padding: "8px 14px", fontSize: 13, fontWeight: 700,
                border: "none",
                background: mode === "edit" ? BRAND.orange : "#fff",
                color: mode === "edit" ? "#fff" : BRAND.textMuted,
                cursor: "pointer",
                borderLeft: `1px solid ${BRAND.border}`,
              }}
            >
              ✏️ Edit
            </button>
          </div>
          <button
            onClick={() => {
              if (calibMode) cancelCalib();
              else {
                setCalibMode(true);
                setCalibP1(null);
                setCalibP2(null);
                setSelectedDevice(null);
                setErr("");
              }
            }}
            title={settings.pixels_per_meter ? "Re-calibrate the visual ruler" : "Optional — sets a visual ruler at the bottom"}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              border: `1px solid ${calibMode ? BRAND.red : BRAND.border}`,
              background: calibMode ? BRAND.red : "#fff",
              color: calibMode ? "#fff" : BRAND.textMuted,
              cursor: "pointer",
            }}
          >
            {calibMode ? "Cancel scale" : settings.pixels_per_meter ? "Ruler ✓" : "Optional: Set ruler"}
          </button>
          {lastSaved && !dirty && (
            <div style={{ fontSize: 12, color: BRAND.green }}>
              Saved {new Date(lastSaved).toLocaleTimeString()}
            </div>
          )}
          {dirty && (
            <div style={{ fontSize: 12, color: BRAND.red, fontWeight: 600 }}>
              Unsaved layout
            </div>
          )}
          <button
            disabled={!dirty || saving}
            onClick={save}
            style={{
              padding: "10px 22px",
              fontSize: 15,
              fontWeight: 700,
              borderRadius: 8,
              border: "none",
              background: !dirty || saving ? "#9ca3af" : BRAND.orange,
              color: "#fff",
              cursor: !dirty || saving ? "not-allowed" : "pointer",
              minHeight: 40,
            }}
          >
            {saving ? "Saving…" : "Save Layout"}
          </button>
        </div>
      </div>

      {calibMode && !calibAsk && (
        <div
          style={{
            background: "#eff6ff",
            color: BRAND.blue,
            border: `1px solid ${BRAND.blue}`,
            padding: "8px 12px",
            borderRadius: 6,
            marginBottom: 8,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Click {!calibP1 ? "first point" : "second point"} on the canvas (any
          two points whose real distance you know — used only for the ruler).
        </div>
      )}

      {err && (
        <div
          style={{
            background: "#fee2e2",
            color: BRAND.red,
            border: `1px solid ${BRAND.red}`,
            padding: "8px 12px",
            borderRadius: 6,
            marginBottom: 8,
            fontSize: 13,
          }}
        >
          {err}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: BRAND.textMuted }}>
          Loading machines…
        </div>
      ) : (
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {/* Palette */}
          <div
            style={{
              width: 260,
              background: BRAND.bg,
              border: `1px solid ${BRAND.border}`,
              borderRadius: 8,
              padding: 10,
              maxHeight: CANVAS_H + 2,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: BRAND.textMuted,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              Unplaced ({unplacedCount})
            </div>
            {palette.length === 0 && (
              <div style={{ fontSize: 12, color: BRAND.textMuted, padding: 8 }}>
                All machines placed.
              </div>
            )}
            {palette.map((bucket) => (
              <div key={bucket.groupId || "__ungrouped__"} style={{ marginBottom: 12 }}>
                <div
                  onClick={() => placeGroup(bucket.groupId)}
                  title="Click to place all machines in this cell"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    background: bucket.color.bg,
                    border: `1px solid ${bucket.color.border}`,
                    borderRadius: 6,
                    cursor: "pointer",
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      width: 10, height: 10,
                      background: bucket.color.border,
                      borderRadius: 2,
                      flexShrink: 0,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 13, fontWeight: 700, color: BRAND.textDark,
                      flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {bucket.groupName} ({bucket.machines.length})
                  </div>
                  <span style={{ color: bucket.color.border, fontWeight: 700, fontSize: 16 }}>＋</span>
                </div>
                {bucket.machines.map((m) => (
                  <div
                    key={m.device_id}
                    onClick={() => placeMachine(m.device_id)}
                    title="Click to add to layout"
                    style={{
                      marginLeft: 16,
                      padding: "8px 10px",
                      marginBottom: 4,
                      background: "#ffffff",
                      border: `1px dashed ${bucket.color.border}`,
                      borderRadius: 5,
                      cursor: "pointer",
                      fontSize: 12, fontWeight: 600, color: BRAND.textDark,
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.name}
                    </span>
                    <span style={{ color: bucket.color.border, fontSize: 15 }}>＋</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Canvas */}
          <div style={{ position: "relative" }}>
            <div
              ref={canvasRef}
              onClick={onCanvasClick}
              onMouseMove={onCanvasMouseMove}
              onMouseLeave={() => setDrawCursor(null)}
              style={{
                position: "relative",
                width: CANVAS_W,
                height: CANVAS_H,
                background: `
                  linear-gradient(to right, ${BRAND.border} 1px, transparent 1px) 0 0 / ${GRID}px ${GRID}px,
                  linear-gradient(to bottom, ${BRAND.border} 1px, transparent 1px) 0 0 / ${GRID}px ${GRID}px,
                  #ffffff
                `,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 8,
                overflow: "hidden",
                userSelect: "none",
                cursor: calibMode || selectedDevice ? "crosshair" : "default",
              }}
            >
              {/* Cell hulls — dashed rectangle + tab label per group */}
              {hulls.map((h) => (
                <div
                  key={`hull-${h.groupId}`}
                  style={{
                    position: "absolute",
                    left: h.x, top: h.y, width: h.w, height: h.h,
                    background: h.color.bg,
                    border: `2px dashed ${h.color.border}`,
                    borderRadius: 12,
                    opacity: 0.55,
                    pointerEvents: "none",
                  }}
                />
              ))}
              {hulls.map((h) => {
                const labelH = 20;
                const above = h.y - labelH - 2;
                const useAbove = above >= 2;
                return (
                  <div
                    key={`hull-label-${h.groupId}`}
                    style={{
                      position: "absolute",
                      left: h.x,
                      top: useAbove ? above : h.y + 2,
                      background: h.color.border,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: useAbove ? "6px 6px 0 0" : 4,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      pointerEvents: "none",
                      zIndex: 3,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                      lineHeight: 1.2,
                    }}
                  >
                    {h.groupName}
                  </div>
                );
              })}

              {/* Saved path polylines */}
              <svg
                width={CANVAS_W}
                height={CANVAS_H}
                style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", zIndex: 2 }}
              >
                {pathSegments
                  .filter((s) => s.status !== "blocked")
                  .map((s) => {
                    const ptsAttr = s.polyPoints.map((p) => `${p.x},${p.y}`).join(" ");
                    const isSelected = selectedPathKey === s.pairKey;
                    const focusDevice = viewSelectedDevice || hoveredDevice;
                    const touchesFocus = focusDevice && (s.deviceA === focusDevice || s.deviceB === focusDevice);
                    const dim = (selectedPathKey && !isSelected) || (focusDevice && !touchesFocus && !isSelected);
                    return (
                      <polyline
                        key={`pl-${s.pairKey}`}
                        points={ptsAttr}
                        fill="none"
                        stroke={isSelected ? BRAND.blue : (touchesFocus ? BRAND.blue : BRAND.green)}
                        strokeOpacity={dim ? 0.2 : (isSelected ? 0.95 : (touchesFocus ? 0.95 : 0.85))}
                        strokeWidth={(isSelected || touchesFocus) ? 3.5 : 2.5}
                        strokeLinejoin="round"
                      />
                    );
                  })}
                {/* Optional calibration line (visible only while ruler is set) */}
                {settings.pixels_per_meter &&
                  settings.scale_p1_x !== null &&
                  settings.scale_p2_x !== null && (
                    <line
                      x1={settings.scale_p1_x}
                      y1={settings.scale_p1_y}
                      x2={settings.scale_p2_x}
                      y2={settings.scale_p2_y}
                      stroke={BRAND.green}
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                      opacity="0.4"
                    />
                  )}
                {/* Live calibration line while picking */}
                {calibMode && calibP1 && (
                  <>
                    <circle cx={calibP1.x} cy={calibP1.y} r="6" fill={BRAND.blue} />
                    {calibP2 && (
                      <>
                        <circle cx={calibP2.x} cy={calibP2.y} r="6" fill={BRAND.blue} />
                        <line
                          x1={calibP1.x} y1={calibP1.y}
                          x2={calibP2.x} y2={calibP2.y}
                          stroke={BRAND.blue} strokeWidth="2"
                        />
                      </>
                    )}
                  </>
                )}

                {/* Live preview while drawing a path */}
                {selectedDevice && !calibMode && drawCursor && settings.pixels_per_meter && (
                  <g pointerEvents="none">
                    <rect
                      x={drawCursor.x + 14}
                      y={drawCursor.y - 22}
                      width={(`${(drawLivePx / settings.pixels_per_meter).toFixed(1)} m`).length * 8 + 16}
                      height={20}
                      rx={4}
                      fill="#111827"
                      opacity="0.92"
                    />
                    <text
                      x={drawCursor.x + 22}
                      y={drawCursor.y - 8}
                      fill="#fff"
                      fontSize="12"
                      fontWeight="700"
                      fontFamily="system-ui, sans-serif"
                    >
                      {(drawLivePx / settings.pixels_per_meter).toFixed(1)} m
                    </text>
                  </g>
                )}
                {selectedDevice && !calibMode && (() => {
                  const start = placedById.get(selectedDevice);
                  if (!start) return null;
                  const sx = start.x + TILE_W / 2;
                  const sy = start.y + TILE_H / 2;
                  const pts = [
                    { x: sx, y: sy },
                    ...drawWaypoints,
                    ...(drawCursor ? [drawCursor] : []),
                  ];
                  const ptsAttr = pts.map((p) => `${p.x},${p.y}`).join(" ");
                  return (
                    <>
                      <polyline
                        points={ptsAttr}
                        fill="none"
                        stroke={BRAND.blue}
                        strokeWidth="3"
                        strokeDasharray="6 5"
                        strokeOpacity="0.85"
                        strokeLinejoin="round"
                      />
                      {drawWaypoints.map((w, i) => (
                        <circle
                          key={`dwp-${i}`}
                          cx={w.x} cy={w.y} r="6"
                          fill={BRAND.blue} stroke="#fff" strokeWidth="2"
                        />
                      ))}
                    </>
                  );
                })()}
              </svg>

              {/* Path labels — click once to select (show handles), click again to edit value */}
              {pathSegments
                .filter((s) => s.status !== "blocked")
                .map((s) => {
                  const isSel = selectedPathKey === s.pairKey;
                  const focusDevice = viewSelectedDevice || hoveredDevice;
                  const touchesFocus = focusDevice && (s.deviceA === focusDevice || s.deviceB === focusDevice);
                  const dim = (selectedPathKey && !isSel) || (focusDevice && !touchesFocus && !isSel);
                  const labelBg = isSel ? BRAND.blue : (touchesFocus ? BRAND.blue : BRAND.green);
                  return (
                    <div
                      key={`pll-${s.pairKey}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (mode === "view") return;
                        if (isSel) {
                          openLabelModal(s);
                        } else {
                          setSelectedPathKey(s.pairKey);
                        }
                      }}
                      title={mode === "view"
                        ? "Switch to Edit mode to change paths"
                        : (isSel ? "Click again to edit value" : "Click to select path")}
                      style={{
                        position: "absolute",
                        left: s.midX, top: s.midY,
                        transform: "translate(-50%, -50%)",
                        background: labelBg,
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 6,
                        zIndex: 4,
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        opacity: dim ? 0.5 : 1,
                        transition: "opacity 120ms, background 120ms",
                        boxShadow: (isSel || touchesFocus)
                          ? "0 0 0 3px rgba(37,99,235,0.25), 0 1px 4px rgba(0,0,0,0.2)"
                          : "0 1px 4px rgba(0,0,0,0.2)",
                      }}
                    >
                      {`${s.meters ?? "?"} m`}
                    </div>
                  );
                })}

              {/* Waypoint handles + insert points — only for the selected path */}
              {selectedPathKey && (() => {
                const seg = pathSegments.find((s) => s.pairKey === selectedPathKey);
                if (!seg) return null;
                const handles = [];
                // Existing waypoints (draggable)
                seg.waypoints.forEach((w, i) => {
                  handles.push(
                    <div
                      key={`wp-${seg.pairKey}-${i}`}
                      onMouseDown={(e) => onWaypointMouseDown(e, seg.pairKey, i)}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        removeWaypoint(seg.pairKey, i);
                      }}
                      title="Drag to bend • double-click to remove"
                      style={{
                        position: "absolute",
                        left: w.x - 8,
                        top: w.y - 8,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: BRAND.blue,
                        border: "2px solid #fff",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                        cursor: "grab",
                        zIndex: 5,
                      }}
                    />,
                  );
                });
                // Insert-bend handles at midpoint of each segment
                for (let i = 1; i < seg.polyPoints.length; i += 1) {
                  const p0 = seg.polyPoints[i - 1];
                  const p1 = seg.polyPoints[i];
                  const mx = (p0.x + p1.x) / 2;
                  const my = (p0.y + p1.y) / 2;
                  const insertAt = i - 1; // splice index in waypoints[]
                  handles.push(
                    <div
                      key={`wpins-${seg.pairKey}-${i}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        insertWaypoint(seg.pairKey, insertAt, {
                          x: Math.round(mx),
                          y: Math.round(my),
                        });
                      }}
                      title="Click to add a bend here"
                      style={{
                        position: "absolute",
                        left: mx - 7,
                        top: my - 7,
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: "#fff",
                        border: `2px dashed ${BRAND.blue}`,
                        color: BRAND.blue,
                        fontSize: 12,
                        fontWeight: 800,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "copy",
                        zIndex: 4,
                        lineHeight: 1,
                      }}
                    >＋</div>,
                  );
                }
                return handles;
              })()}

              {/* Placed tiles */}
              {placed.map((m) => {
                const c = colorFor(m.group_id);
                const isSelected = selectedDevice === m.device_id;
                return (
                  <div
                    key={m.device_id}
                    onMouseDown={(e) => onTileMouseDown(e, m)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTileClick(m);
                    }}
                    onMouseEnter={() => {
                      if (!drag.current.deviceId && !selectedDevice) {
                        setHoveredDevice(m.device_id);
                      }
                    }}
                    onMouseLeave={() => {
                      setHoveredDevice((h) => (h === m.device_id ? null : h));
                    }}
                    title="Hover to see this machine's paths · click to start drawing a path"
                    style={{
                      position: "absolute",
                      left: m.x, top: m.y,
                      width: TILE_W, height: TILE_H,
                      background: c.bg,
                      border: `${isSelected ? 3 : 2}px solid ${isSelected ? BRAND.blue : c.border}`,
                      borderRadius: 6,
                      boxShadow: isSelected
                        ? `0 0 0 4px ${BRAND.blue}33, 0 2px 4px rgba(0,0,0,0.1)`
                        : "0 2px 4px rgba(0,0,0,0.08)",
                      cursor: calibMode ? "crosshair" : "move",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 6,
                      textAlign: "center",
                      fontSize: 12, fontWeight: 700, color: BRAND.textDark,
                      zIndex: isSelected ? 5 : 1,
                    }}
                  >
                    <div style={{ wordBreak: "break-word", lineHeight: 1.2 }}>{m.name}</div>
                    {m.group_name && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: 10, fontWeight: 600,
                          color: c.border,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        {m.group_name}
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        unplaceMachine(m.device_id);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      title="Remove from layout"
                      style={{
                        position: "absolute",
                        top: 2, right: 2,
                        width: 18, height: 18,
                        border: "none",
                        background: "transparent",
                        color: BRAND.textMuted,
                        cursor: "pointer",
                        fontSize: 16, lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>

            {(viewSelectedDevice || hoveredDevice) && !selectedDevice && !selectedPathKey && (() => {
              const focusId = viewSelectedDevice || hoveredDevice;
              const hov = placedById.get(focusId);
              if (!hov) return null;
              const others = placed.filter((m) => m.device_id !== focusId);
              const connected = [];
              const missing = [];
              for (const m of others) {
                const k = pairKey(focusId, m.device_id);
                const seg = pathways[k];
                if (seg && seg.status !== "blocked" && seg.meters != null) {
                  connected.push({ name: m.name, meters: seg.meters });
                } else {
                  missing.push({ name: m.name });
                }
              }
              connected.sort((a, b) => a.meters - b.meters);
              const isPinned = !!viewSelectedDevice;
              return (
                <div
                  style={{
                    position: "absolute",
                    right: 12, top: 12,
                    background: "rgba(255,255,255,0.97)",
                    border: `1px solid ${BRAND.blue}`,
                    padding: "10px 12px",
                    borderRadius: 8,
                    fontSize: 12,
                    color: BRAND.textDark,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    zIndex: 10,
                    minWidth: 220,
                    maxWidth: 280,
                    pointerEvents: isPinned ? "auto" : "none",
                  }}
                >
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>
                    Distances from {hov.name}
                  </div>
                  {connected.length > 0 && (
                    <div style={{ marginBottom: missing.length ? 8 : 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.green, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                        Connected ({connected.length})
                      </div>
                      {connected.map((c) => (
                        <div key={c.name} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "2px 0" }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                          <span style={{ color: BRAND.green, fontWeight: 700 }}>{c.meters} m</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {missing.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                        Not drawn yet ({missing.length})
                      </div>
                      {missing.map((c) => (
                        <div key={c.name} style={{ padding: "2px 0", color: BRAND.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c.name}
                        </div>
                      ))}
                      <div style={{ marginTop: 6, fontSize: 11, color: BRAND.blue, fontStyle: "italic" }}>
                        Click this machine then draw a path to add.
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {selectedPathKey && (() => {
              const seg = pathSegments.find((s) => s.pairKey === selectedPathKey);
              const nameA = seg?.nameA || "";
              const nameB = seg?.nameB || "";
              return (
                <div
                  style={{
                    position: "absolute",
                    right: 12, top: 12,
                    background: "rgba(255,255,255,0.97)",
                    border: `1px solid ${BRAND.blue}`,
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: BRAND.textDark,
                    display: "flex", flexDirection: "column", gap: 6,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    zIndex: 10,
                    maxWidth: 340,
                  }}
                >
                  <div>
                    📍 <b>{nameA} ↔ {nameB}</b>
                  </div>
                  <div style={{ color: BRAND.textMuted, fontSize: 11, lineHeight: 1.4 }}>
                    Drag blue dots to bend · click + to add bend · double-click dot to remove
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <button
                      onClick={() => {
                        if (!seg) return;
                        if (!window.confirm(`Delete the path between ${nameA} and ${nameB}?`)) return;
                        (async () => {
                          const ok = await submitPath({
                            device_a: seg.deviceA,
                            device_b: seg.deviceB,
                            delete: true,
                          });
                          if (ok) {
                            setPathways((p) => {
                              const n = { ...p };
                              delete n[seg.pairKey];
                              return n;
                            });
                            setSelectedPathKey(null);
                            setErr("");
                          }
                        })();
                      }}
                      style={{
                        padding: "4px 10px", fontSize: 12, fontWeight: 700,
                        background: BRAND.red, color: "#fff",
                        border: "none", borderRadius: 4, cursor: "pointer",
                      }}
                    >
                      🗑️ Delete path
                    </button>
                    <button
                      onClick={() => setSelectedPathKey(null)}
                      style={{
                        padding: "4px 10px", fontSize: 12, fontWeight: 700,
                        background: "#fff", border: `1px solid ${BRAND.blue}`,
                        color: BRAND.blue, borderRadius: 4, cursor: "pointer",
                        marginLeft: "auto",
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              );
            })()}

            {selectedDevice && (
              <div
                style={{
                  position: "absolute",
                  left: 12, top: 12,
                  background: "rgba(255,255,255,0.96)",
                  border: `1px solid ${BRAND.blue}`,
                  padding: "8px 12px",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  color: BRAND.textDark,
                  display: "flex", alignItems: "center", gap: 10,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                  zIndex: 10,
                }}
              >
                <span>
                  ✏️ Drawing from <b style={{ color: BRAND.blue }}>{placedById.get(selectedDevice)?.name}</b>
                  {drawWaypoints.length > 0 && <span style={{ color: BRAND.textMuted, marginLeft: 6 }}>· {drawWaypoints.length} bend{drawWaypoints.length === 1 ? "" : "s"}</span>}
                </span>
                <button
                  disabled={drawWaypoints.length === 0}
                  onClick={undoLastWaypoint}
                  style={{
                    padding: "4px 10px", fontSize: 12, fontWeight: 700,
                    background: drawWaypoints.length === 0 ? "#f3f4f6" : "#fff",
                    border: `1px solid ${BRAND.border}`,
                    color: drawWaypoints.length === 0 ? "#cbd5e1" : BRAND.textDark,
                    borderRadius: 4,
                    cursor: drawWaypoints.length === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  ↶ Undo bend
                </button>
                {settings.pixels_per_meter ? (
                  <span
                    style={{
                      background: BRAND.green,
                      color: "#fff",
                      padding: "4px 10px",
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Live: {(drawLivePx / settings.pixels_per_meter).toFixed(1)} m
                  </span>
                ) : (
                  <span
                    style={{
                      background: "#fff8e6",
                      color: "#7a4a00",
                      padding: "4px 10px",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 700,
                      border: "1px solid #f59e0b",
                    }}
                  >
                    Set ruler to auto-measure
                  </span>
                )}
                <button
                  onClick={cancelDraw}
                  style={{
                    padding: "4px 10px", fontSize: 12, fontWeight: 700,
                    background: "#fff",
                    border: `1px solid ${BRAND.red}`,
                    color: BRAND.red, borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {rulerMeters && settings.pixels_per_meter && (
              <div
                style={{
                  position: "absolute",
                  left: 12, bottom: 10,
                  display: "flex", alignItems: "center", gap: 8,
                  background: "rgba(255,255,255,0.92)",
                  border: `1px solid ${BRAND.border}`,
                  padding: "4px 8px", borderRadius: 4,
                }}
              >
                <div
                  style={{
                    width: rulerMeters * settings.pixels_per_meter,
                    height: 6,
                    background: BRAND.textDark,
                    position: "relative",
                  }}
                >
                  <div style={tickStyle("left")} />
                  <div style={tickStyle("right")} />
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.textDark }}>
                  {rulerMeters} m
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Path modal */}
      {pathModal && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(15,23,42,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 420,
              boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: BRAND.textDark }}>
              {pathModal.isExisting ? "Edit path" : "Add path"}
            </div>
            <div style={{ fontSize: 14, color: BRAND.textMuted, marginBottom: 16 }}>
              Between <b style={{ color: BRAND.textDark }}>{pathModal.nameA}</b> and{" "}
              <b style={{ color: BRAND.textDark }}>{pathModal.nameB}</b>
            </div>

            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: BRAND.textDark, marginBottom: 6 }}>
              Walking distance (meters)
            </label>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
              <input
                autoFocus
                type="number"
                min="0"
                step="0.5"
                value={pathModal.meters}
                onChange={(e) => setPathModal({ ...pathModal, meters: e.target.value, status: "walkable" })}
                placeholder="e.g. 8"
                style={{
                  flex: 1,
                  padding: "12px 14px",
                  fontSize: 18,
                  border: `2px solid ${BRAND.border}`,
                  borderRadius: 8,
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") savePathWalkable();
                  if (e.key === "Escape") closeModal();
                }}
              />
              <button
                onClick={savePathWalkable}
                style={{
                  background: BRAND.green,
                  color: "#fff",
                  border: "none",
                  padding: "12px 22px",
                  borderRadius: 8,
                  fontSize: 15, fontWeight: 700,
                  cursor: "pointer",
                  minHeight: 48,
                }}
              >
                Save
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
              {pathModal.isExisting ? (
                <button
                  onClick={deletePath}
                  style={{
                    padding: "8px 14px",
                    background: "#fff",
                    color: BRAND.textMuted,
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Remove path
                </button>
              ) : <span />}
              <button
                onClick={closeModal}
                style={{
                  padding: "8px 14px",
                  background: "#fff",
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scale calibration prompt */}
      {calibAsk && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(15,23,42,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              width: 360,
              boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: BRAND.textDark }}>
              Set the ruler
            </div>
            <div style={{ fontSize: 13, color: BRAND.textMuted, marginBottom: 12 }}>
              How many meters between the two points you just clicked? (Used
              only for the visual ruler at the bottom of the canvas.)
            </div>
            <input
              type="number" autoFocus
              value={calibMeters}
              onChange={(e) => setCalibMeters(e.target.value)}
              placeholder="e.g. 10"
              min="0.1" step="0.1"
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: 16,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 6,
                marginBottom: 12,
                boxSizing: "border-box",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") finishCalib();
                if (e.key === "Escape") cancelCalib();
              }}
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={cancelCalib}
                style={{
                  padding: "8px 14px",
                  background: "#fff",
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={finishCalib}
                style={{
                  padding: "8px 14px",
                  background: BRAND.blue,
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Save Ruler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function tickStyle(side) {
  return {
    position: "absolute",
    top: -4,
    [side]: 0,
    width: 2, height: 14,
    background: "#111827",
  };
}
